import { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useQuizeStore } from "../../stores/quizeStore";
import { PreassessmentQuestion } from "../../axios/quiz";
import { sendPreassessmentBypassStatistic } from "../../axios/chat";
import { QuizContent } from "./quiz-content/quiz-content";
import { QuizFinish } from "./quiz-finish/quiz-finish";
import { useStatisticsStore } from "../../stores/statisticsStore";
import { useAppStore } from "../../stores/appStore";
import { getElapsedSeconds } from "../../utils/assessmentTimeLeft";
import {
  assessmentOverlayCardStyle,
  assessmentPopUpOuterStyle,
  assessmentPopUpShellStyle,
} from "./assessment-layout-styles";

const OFFLINE_ANSWER_MESSAGE =
  "Could not save your answer. Check your connection and tap Check Answer again.";

interface QuizePopUpProps {
  open: boolean;
  onClose: () => void;
}

const MANAGER_BYPASS_ERROR_MESSAGE =
  "Could not complete the pre-assessment. Check your connection and try again.";

export const QuizePopUp = ({ open, onClose }: QuizePopUpProps) => {
  const { preassessmentData, sendAnswer } = useQuizeStore();
  const { setRefreshedLeaderBoard, setRefreshedProgressBar } =
    useStatisticsStore();
  const { domain, focus } = useAppStore();

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentQuestionArrayIndex, setCurrentQuestionArrayIndex] = useState(0);
  const [isAnswerChecked, setIsAnswerChecked] = useState(false);
  const [currentAnswer, setCurrentAnswer] = useState<string | null>(null);
  const [checkedAnswer, setCheckedAnswer] = useState<string | null>(null);
  const [isFinish, setIsFinish] = useState(false);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [timeSpent, setTimeSpent] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompletingPreassessment, setIsCompletingPreassessment] =
    useState(false);
  const [answerSaveError, setAnswerSaveError] = useState<string | null>(null);
  const [connectionRestored, setConnectionRestored] = useState(false);
  const [isNetworkOnline, setIsNetworkOnline] = useState<boolean>(
    typeof navigator === "undefined" ? true : navigator.onLine
  );
  const [wasOffline, setWasOffline] = useState<boolean>(
    typeof navigator === "undefined" ? false : !navigator.onLine
  );
  const bypassNextIndexRef = useRef<number | null>(null);
  const bypassOriginalRemainingRef = useRef<PreassessmentQuestion[] | null>(
    null
  );
  const answeredBeforeBypassRef = useRef<number | null>(null);
  const scoresBeforeBypassRef = useRef<number | null>(null);

  const resetBypassProgress = () => {
    bypassNextIndexRef.current = null;
    bypassOriginalRemainingRef.current = null;
    answeredBeforeBypassRef.current = null;
    scoresBeforeBypassRef.current = null;
  };

  useEffect(() => {
    if (open) {
      const answered = preassessmentData.answered_questions_number;
      const total = preassessmentData.total_questions_number;
      const questionsCount = preassessmentData.questions?.length ?? 0;
      const usesRemainingQuestionsSlice =
        questionsCount > 0 && questionsCount !== total;

      setStartTime(Date.now());
      setIsAnswerChecked(false);
      setCurrentAnswer(null);
      setCheckedAnswer(null);
      setAnswerSaveError(null);
      setConnectionRestored(false);
      setIsCompletingPreassessment(false);
      setCurrentQuestionIndex(answered);
      setCurrentQuestionArrayIndex(usesRemainingQuestionsSlice ? 0 : answered);
      resetBypassProgress();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    setIsAnswerChecked(false);
    setCurrentAnswer(null);
    setCheckedAnswer(null);
    setAnswerSaveError(null);
    setConnectionRestored(false);
  }, [currentQuestionArrayIndex]);

  useEffect(() => {
    const syncNetworkStatus = () => {
      setIsNetworkOnline(navigator.onLine);
    };

    const handleOnline = () => setIsNetworkOnline(true);
    const handleOffline = () => setIsNetworkOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    const pollId = window.setInterval(syncNetworkStatus, 1500);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.clearInterval(pollId);
    };
  }, []);

  useEffect(() => {
    if (!isNetworkOnline) {
      setWasOffline(true);
      return;
    }

    if (!wasOffline) {
      return;
    }

    setWasOffline(false);
    setAnswerSaveError(null);
    setConnectionRestored(true);

    const hideTimeout = setTimeout(() => {
      setConnectionRestored(false);
    }, 10000);

    return () => clearTimeout(hideTimeout);
  }, [isNetworkOnline, wasOffline]);

  if (!open) {
    return null;
  }

  if (
    !preassessmentData.questions ||
    preassessmentData.questions.length === 0
  ) {
    return (
      <Box sx={assessmentPopUpOuterStyle}>
        <Box
          sx={{
            ...assessmentOverlayCardStyle,
            background: "#FFFFFF",
            zIndex: 100,
            alignItems: "center",
            justifyContent: "center",
            p: 4,
          }}
        >
          <Typography variant="h6">Loading questions...</Typography>
        </Box>
      </Box>
    );
  }

  const totalQuestions = preassessmentData.total_questions_number;

  const currentQuestion: PreassessmentQuestion = preassessmentData.questions[0];
  const resolvedCurrentQuestion: PreassessmentQuestion =
    preassessmentData.questions[currentQuestionArrayIndex] ?? currentQuestion;

  const handleAnswerSelect = (answer: string) => {
    setCurrentAnswer(answer);
  };

  const handleCheckAnswer = async () => {
    if (!currentAnswer?.trim()) return;
    if (isSubmitting || isCompletingPreassessment) return;
    if (!navigator.onLine) return;

    setCheckedAnswer(currentAnswer);
    setIsSubmitting(true);
    setIsAnswerChecked(true);

    const selectedAnswerObj = resolvedCurrentQuestion.answers.find(
      (ans) => ans.text === currentAnswer
    );
    const selectedConcept = resolvedCurrentQuestion.competency;
    if (selectedAnswerObj) {
      const totalScore = resolvedCurrentQuestion.answers.reduce(
        (acc, ans) => acc + ans.points,
        0
      );
      try {
        await sendAnswer({
          concept: selectedConcept,
          score: selectedAnswerObj.points,
          all_score: totalScore,
        });
      } catch (error) {
        console.error("Error sending answer:", error);
        setAnswerSaveError(OFFLINE_ANSWER_MESSAGE);
      }
    }
    setIsSubmitting(false);
  };

  const handleNextQuestion = () => {
    setIsAnswerChecked(false);
    setCheckedAnswer(null);
    setCurrentQuestionIndex((prev) => prev + 1);
    setCurrentQuestionArrayIndex((prev) => prev + 1);
    setCurrentAnswer(null);
  };

  const handleFinish = () => {
    if (startTime) {
      setTimeSpent(getElapsedSeconds(startTime));
    }
    setRefreshedLeaderBoard(true);
    setRefreshedProgressBar(true);
    setIsFinish(true);
  };

  const handleCompletePreassessment = async () => {
    if (isSubmitting || isCompletingPreassessment) return;
    if (!navigator.onLine) return;

    const questions = preassessmentData.questions;
    if (!questions?.length) return;

    const defaultStartIndex = isAnswerChecked
      ? currentQuestionArrayIndex + 1
      : currentQuestionArrayIndex;
    const startIndex = bypassNextIndexRef.current ?? defaultStartIndex;
    const remainingQuestions = questions.slice(startIndex);

    if (remainingQuestions.length === 0) {
      resetBypassProgress();
      handleFinish();
      return;
    }

    if (bypassNextIndexRef.current === null) {
      bypassOriginalRemainingRef.current = remainingQuestions;
      answeredBeforeBypassRef.current =
        preassessmentData.answered_questions_number;
      scoresBeforeBypassRef.current = preassessmentData.scores_earned;
    }

    setIsCompletingPreassessment(true);
    setAnswerSaveError(null);
    setConnectionRestored(false);

    try {
      for (let i = 0; i < remainingQuestions.length; i++) {
        const question = remainingQuestions[i];
        const absoluteIndex = startIndex + i;
        const allScore = question.answers.reduce(
          (acc, ans) => acc + ans.points,
          0
        );
        await sendAnswer({
          concept: question.competency,
          score: 5,
          all_score: allScore,
        });
        bypassNextIndexRef.current = absoluteIndex + 1;
      }

      const bypassedQuestions =
        bypassOriginalRemainingRef.current ?? remainingQuestions;
      const answeredBeforeBypass =
        answeredBeforeBypassRef.current ??
        preassessmentData.answered_questions_number;
      const scoresBeforeBypass =
        scoresBeforeBypassRef.current ?? preassessmentData.scores_earned;

      try {
        await sendPreassessmentBypassStatistic({
          domain,
          focus,
          total_questions: totalQuestions,
          answered_before_bypass: answeredBeforeBypass,
          scores_earned_before_bypass: scoresBeforeBypass,
          bypassed_questions: bypassedQuestions,
        });
      } catch (error) {
        console.error(
          "Error sending preassessment bypass statistic:",
          error
        );
      }

      resetBypassProgress();
      setCurrentQuestionIndex(totalQuestions - 1);
      setCurrentQuestionArrayIndex(questions.length - 1);
      setIsAnswerChecked(true);
      handleFinish();
    } catch (error) {
      console.error("Error completing pre-assessment:", error);
      setAnswerSaveError(MANAGER_BYPASS_ERROR_MESSAGE);
    } finally {
      setIsCompletingPreassessment(false);
    }
  };

  const handleClose = async () => {
    resetBypassProgress();
    setIsFinish(false);
    onClose();
  };

  const isAnswerSelected = Boolean(currentAnswer?.trim());
  const connectionErrorMessage =
    !isNetworkOnline || answerSaveError
      ? answerSaveError ?? OFFLINE_ANSWER_MESSAGE
      : null;

  return (
    <Box sx={assessmentPopUpOuterStyle}>
      <Box sx={assessmentPopUpShellStyle}>
        {!isFinish ? (
          <QuizContent
            currentQuestion={resolvedCurrentQuestion}
            currentQuestionIndex={currentQuestionIndex}
            totalQuestions={totalQuestions}
            selectedAnswer={checkedAnswer || currentAnswer || ""}
            isAnswerChecked={isAnswerChecked}
            isAnswerSelected={isAnswerSelected}
            isSubmitting={isSubmitting}
            isCompletingPreassessment={isCompletingPreassessment}
            onAnswerSelect={handleAnswerSelect}
            onCheckAnswer={handleCheckAnswer}
            onNextQuestion={handleNextQuestion}
            onFinish={handleFinish}
            onClose={onClose}
            onCompletePreassessment={handleCompletePreassessment}
            isCheckAnswerBlocked={!isNetworkOnline}
            connectionErrorMessage={connectionErrorMessage}
            connectionRestored={connectionRestored}
          />
        ) : (
          <QuizFinish
            totalQuestions={totalQuestions}
            currentQuestionIndex={currentQuestionIndex}
            onClose={handleClose}
            continueLabel="Continue conversation"
            timeSpent={timeSpent}
            fullScreenOnMobile
          />
        )}
      </Box>
    </Box>
  );
};

export default QuizePopUp;
